import type { GuestAwareUser, GuestSelectedProfile } from './types';
import { GUEST_ALLOWED_PRIMARY_SEGMENTS } from './constants';

export function guestPrimaryRouteSegment(pathname: string): string | undefined {
    const trimmed = pathname.replace(/^\/+/, '');
    const segments = trimmed.split('/').filter(Boolean);
    return segments[0] === 'en' ? segments[1] : segments[0];
}

export function isGuestNavigationAllowed(item: { guestModeNavigationAllowed?: boolean | string } | undefined): boolean {
    return item?.guestModeNavigationAllowed === true || item?.guestModeNavigationAllowed === 'true';
}

export function isGuestVisibleCarousel(carousel: {
    active?: boolean;
    guestModeActive?: boolean;
    wcpType?: string;
    type?: string;
}): boolean {
    if (!carousel?.active) {
        return false;
    }
    if (carousel.guestModeActive === true) {
        return true;
    }
    const type = carousel.wcpType || carousel.type;
    const GUEST_CAROUSEL_TYPE_EXEMPTIONS = new Set<string>(['SEARCH_RESULTS']);
    return typeof type === 'string' && GUEST_CAROUSEL_TYPE_EXEMPTIONS.has(type);
}

export function isGuestVisibleMenuItem(item: { guestModeActive?: boolean }): boolean {
    return item.guestModeActive === true;
}

export function canGuestOpenMenuItem(item: { guestModeNavigationAllowed?: boolean | string } | undefined): boolean {
    return isGuestNavigationAllowed(item);
}

function normalizeGuestMenuScreen(screen?: string): string {
    if (!screen) {
        return '';
    }
    if (screen === 'selectProfile') {
        return 'select-profile';
    }
    return screen.toLowerCase();
}

export function getGuestOpenScreensFromMenu(
    menuItems:
        | Array<{
              screen?: string;
              screenQueryParams?: string;
              guestModeNavigationAllowed?: boolean | string;
          }>
        | undefined
): Set<string> {
    const screens = new Set<string>();

    menuItems?.forEach((item) => {
        if (!isGuestNavigationAllowed(item)) {
            return;
        }

        const screen = normalizeGuestMenuScreen(item.screen);
        if (screen) {
            screens.add(screen);
        }

        const opensKids =
            item.screenQueryParams?.includes('kids=true') &&
            (screen === 'select-profile' || screen === 'kids-home' || screen === 'kids');

        if (opensKids || screen === 'kids-home' || screen === 'kids') {
            screens.add('kids-home');
            screens.add('kids');
        }
    });

    return screens;
}

export function isGuestUser(user: GuestAwareUser | undefined): boolean {
    return user?.isGuest === true;
}

/** Empty `{}` or other persisted user records without a username (not guest). */
export function isInvalidPersistedUser(user: unknown): boolean {
    if (user === null || user === undefined) {
        return false;
    }
    if (typeof user !== 'object') {
        return true;
    }
    const record = user as { username?: string; isGuest?: boolean };
    if (record.isGuest === true) {
        return false;
    }
    return !record.username;
}


export function userHasCatalogAccess(user: GuestAwareUser | undefined): boolean {
    return Boolean(user);
}

export function isGuestBrowseProfile(profile: { profileGuid?: string } | null | undefined): boolean {
    return profile?.profileGuid === 'guest-browse-profile';
}

export function isGuestKidsRoute(segment: string | undefined): boolean {
    const normalized = segment?.toLowerCase();
    return normalized === 'kids-home' || normalized === 'kids';
}

/** CMS kids menu: /select-profile?kids=true */
export function isGuestKidsEntryPath(pathname: string, search = ''): boolean {
    const primary = guestPrimaryRouteSegment(pathname)?.toLowerCase();
    if (primary !== 'select-profile') {
        return false;
    }
    const query = search.startsWith('?') ? search.slice(1) : search;
    return new URLSearchParams(query).get('kids') === 'true';
}

export function isGuestAllowedPrimarySegment(
    segment: string | undefined,
    accessibleSegments: Set<string>
): boolean {
    if (segment === undefined || segment === '') {
        return true;
    }
    const normalized = segment.toLowerCase();
    if (GUEST_ALLOWED_PRIMARY_SEGMENTS.has(normalized)) {
        return true;
    }
    return accessibleSegments.has(normalized);
}

/** Whether a header nav href is reachable for guest browse (home + CMS-allowed screens). */
export function isGuestAllowedHeaderHref(href: string | undefined, accessibleSegments: Set<string>): boolean {
    if (!href) {
        return true;
    }
    const path = href.split('?')[0];
    return isGuestAllowedPrimarySegment(guestPrimaryRouteSegment(path), accessibleSegments);
}

export function mainAppRouteConditionMet(
    user: GuestAwareUser | undefined,
    selectedProfile: GuestSelectedProfile
): boolean {
    if (!user) {
        return false;
    }
    if (isGuestUser(user)) {
        return true;
    }
    if (isGuestBrowseProfile(selectedProfile)) {
        return false;
    }
    return Boolean(selectedProfile?.profileGuid);
}

/** Signed-in and guest users may access main routes; profile selection is enforced separately. */
export function loggedInUserRouteConditionMet(user: GuestAwareUser | undefined, _profilesCount?: number): boolean {
    return Boolean(user);
}
